import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    User, Mail, Phone, Shield, Moon, Sun, Monitor,
    Edit2, Lock, Clock, Calendar, Hash, Layers,
    Camera, Check, X, LogOut, Loader2, ChevronDown, Eye, EyeOff,
    AlertCircle, CheckCircle, AlertTriangle
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import authAxios from "../../api/authAxios";
import { toast } from "react-hot-toast";

export default function SuperAdminProfile() {
    const { user, refreshProfile } = useAuth();
    const { theme, setTheme } = useTheme();

    React.useEffect(() => {
        refreshProfile();
    }, [refreshProfile]);

    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingPassword, setLoadingPassword] = useState(false);

    const [profileData, setProfileData] = useState({
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        phone: user?.phone || "",
    });

    const [passwordData, setPasswordData] = useState({
        old_password: "",
        new_password: "",
        confirm_password: "",
    });

    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [passwordError, setPasswordError] = useState("");
    const [passwordSuccess, setPasswordSuccess] = useState("");

    const [showLogoutConfirmModal, setShowLogoutConfirmModal] = useState(false);
    const [loggingOutSessions, setLoggingOutSessions] = useState(false);

    const getCurrentClientSession = () => {
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : "";
        let device = "Windows PC";
        if (/Windows/i.test(userAgent)) device = "Windows PC";
        else if (/Macintosh|Mac OS/i.test(userAgent)) device = "Mac";
        else if (/Linux/i.test(userAgent)) device = "Linux PC";
        else if (/Android/i.test(userAgent)) device = "Android Mobile";
        else if (/iPhone|iPad/i.test(userAgent)) device = "iPhone";

        let browser = "Browser";
        if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) browser = "Chrome";
        else if (/Firefox/i.test(userAgent)) browser = "Firefox";
        else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = "Safari";
        else if (/Edg/i.test(userAgent)) browser = "Edge";

        return {
            session_key: "current",
            ip_address: "Current Session",
            device: `${browser} – ${device}`,
            last_active: "Active Now",
            is_current: true,
            location: "Current Session"
        };
    };

    const [activeSessions, setActiveSessions] = useState([getCurrentClientSession()]);
    const [loadingSessions, setLoadingSessions] = useState(false);

    React.useEffect(() => {
        if (user) {
            setProfileData({
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                email: user.email || "",
                phone: user.phone || ""
            });
            fetchSessions();
        }
    }, [user]);

    const fetchSessions = async () => {
        try {
            setLoadingSessions(true);
            const res = await authAxios.get('/api/auth/profile/sessions/', { timeout: 5000 });
            if (Array.isArray(res.data) && res.data.length > 0) {
                setActiveSessions(res.data);
            }
        } catch (error) {
            console.warn("Using instant client session fallback for active devices:", error.message || error);
        } finally {
            setLoadingSessions(false);
        }
    };

    const handleLogoutAll = () => {
        setShowLogoutConfirmModal(true);
    };

    const confirmLogoutAllSessions = async () => {
        setLoggingOutSessions(true);
        try {
            await authAxios.post('/api/auth/profile/sessions/');
            toast.success("Logged out from all devices");
            setShowLogoutConfirmModal(false);
            fetchSessions();
        } catch (error) {
            toast.error("Failed to logout sessions");
        } finally {
            setLoggingOutSessions(false);
        }
    };

    const themeOptions = [
        { id: 'light', icon: Sun, label: 'Light' },
        { id: 'dark', icon: Moon, label: 'Dark' },
        { id: 'system', icon: Monitor, label: 'System' }
    ];

    const formatDate = (dateString) => {
        if (!dateString) return "Never";
        return new Date(dateString).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // ... existing handleProfileUpdate ...


    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Send JSON — backend ProfileUpdateSerializer does NOT handle multipart
            // Using FormData was causing the server to receive an empty/unparsed body
            const payload = {
                first_name: profileData.first_name.trim(),
                last_name: profileData.last_name.trim(),
                email: profileData.email.trim(),
                phone: profileData.phone.trim(),
            };

            const response = await authAxios.patch('/api/auth/profile/', payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            // Immediately sync local state from what the server saved
            if (response.data?.user) {
                const saved = response.data.user;
                setProfileData({
                    first_name: saved.first_name || "",
                    last_name: saved.last_name || "",
                    email: saved.email || "",
                    phone: saved.phone || "",
                });
            }

            // Also refresh AuthContext so name in header updates everywhere
            await refreshProfile();

            toast.success("Profile updated successfully!");
            setIsEditing(false);
        } catch (error) {
            const errorData = error.response?.data;
            if (errorData && typeof errorData === 'object') {
                const messages = Object.entries(errorData)
                    .map(([field, msgs]) =>
                        `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`
                    )
                    .join(' | ');
                toast.error(messages || "Failed to update profile");
            } else {
                toast.error(error.response?.data?.detail || "Failed to update profile. Please try again.");
            }
            console.error("Profile update error:", error.response?.data || error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError("");
        setPasswordSuccess("");

        if (!passwordData.old_password) {
            const msg = "Please enter your current password";
            setPasswordError(msg);
            toast.error(msg);
            return;
        }
        if (!passwordData.new_password || passwordData.new_password.length < 6) {
            const msg = "New password must be at least 6 characters long";
            setPasswordError(msg);
            toast.error(msg);
            return;
        }
        if (passwordData.new_password !== passwordData.confirm_password) {
            const msg = "New passwords do not match";
            setPasswordError(msg);
            toast.error(msg);
            return;
        }

        setLoadingPassword(true);
        try {
            const res = await authAxios.post('/api/auth/profile/change-password/', passwordData);
            const msg = res.data?.detail || "Password changed successfully!";
            toast.success(msg);
            setPasswordSuccess(msg);
            setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
            setTimeout(() => {
                setIsChangingPassword(false);
                setPasswordSuccess("");
            }, 1800);
        } catch (error) {
            const errorData = error.response?.data;
            let errMsg = "Failed to change password. Please verify your current password.";

            if (typeof errorData === 'string') {
                errMsg = errorData;
            } else if (errorData?.detail) {
                errMsg = errorData.detail;
            } else if (errorData && typeof errorData === 'object') {
                errMsg = Object.entries(errorData)
                    .map(([field, msgs]) => `${field.replace('_', ' ')}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
                    .join(' | ');
            }

            setPasswordError(errMsg);
            toast.error(errMsg);
        } finally {
            setLoadingPassword(false);
        }
    };

    return (
        <div className="space-y-6 w-full px-6 lg:px-8 pb-12">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Profile Settings</h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">Manage your administrative identity and security</p>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold shadow-lg shadow-slate-900/10 dark:shadow-white/5 transition-all active:scale-95 border border-slate-900 dark:border-white"
                    >
                        <Edit2 className="w-4 h-4" />
                        Edit Profile
                    </button>
                )}
            </div>

            {/* Main Identity Card - Full Width at Top */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl overflow-hidden shadow-xl"
            >
                <div className="h-12 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700" />
                <div className="px-8 pb-8 mt-4">
                    <div className="flex flex-col md:flex-row md:items-end gap-6 mb-8">
                        <div className="space-y-1 py-1">
                            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
                                {user?.first_name} {user?.last_name}
                            </h2>
                            <div className="flex items-center gap-2">
                                <span className="px-3 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[10px] font-black rounded-full uppercase tracking-widest border border-slate-200 dark:border-slate-600">
                                    Super Admin
                                </span>
                                <span className="px-3 py-1 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white text-[10px] font-black rounded-full uppercase tracking-widest border border-slate-200 dark:border-slate-600">
                                    Active
                                </span>
                            </div>
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        {isEditing ? (
                            <motion.form
                                key="edit-form"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onSubmit={handleProfileUpdate}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                            >
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">First Name</label>
                                    <input
                                        type="text"
                                        value={profileData.first_name}
                                        onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
                                    <input
                                        type="text"
                                        value={profileData.last_name}
                                        onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                                    <input
                                        type="email"
                                        value={profileData.email}
                                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        value={profileData.phone}
                                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    />
                                </div>
                                <div className="md:col-span-2 lg:col-span-4 flex justify-end gap-4 mt-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsEditing(false)}
                                        className="px-6 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-8 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold shadow-lg shadow-slate-900/10 flex items-center gap-2 hover:bg-slate-800 dark:hover:bg-slate-100 transition-all disabled:opacity-50"
                                    >
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                        Save Changes
                                    </button>
                                </div>
                            </motion.form>
                        ) : (
                            <motion.div
                                key="view-grid"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
                            >
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">Phone Number</label>
                                    <div className="flex items-center gap-3 text-slate-800 dark:text-slate-200">
                                        <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700/50">
                                            <Phone className="w-4 h-4 text-slate-500" />
                                        </div>
                                        <span className="text-lg font-medium">{user?.phone || "Not Set"}</span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">Email Address</label>
                                    <div className="flex items-center gap-3 text-slate-800 dark:text-slate-200">
                                        <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700/50">
                                            <Mail className="w-4 h-4 text-slate-500" />
                                        </div>
                                        <span className="text-lg font-medium">{user?.email || "Not Set"}</span>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            {/* Row 2: Balanced Info Grid (Equal Height) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Account Details */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-8 shadow-lg flex flex-col h-full"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300">
                            <Layers className="w-5 h-5" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Account Info</h3>
                    </div>
                    <div className="space-y-6 flex-1">
                        <div className="flex justify-between items-center py-2 border-b border-slate-50 dark:border-slate-700/50">
                            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
                                <Hash className="w-4 h-4" />
                                <span>Admin ID</span>
                            </div>
                            <span className="font-mono text-slate-900 dark:text-white font-bold">SA-{user?.id?.toString().padStart(4, '0')}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-slate-50 dark:border-slate-700/50">
                            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
                                <Calendar className="w-4 h-4" />
                                <span>Created On</span>
                            </div>
                            <span className="text-slate-900 dark:text-white font-bold">{formatDate(user?.date_joined)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                            <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400 text-sm">
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span>System Version</span>
                            </div>
                            <span className="px-3 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg text-xs font-bold">v2.4.0 (Stable)</span>
                        </div>
                    </div>
                </motion.div>

                {/* Login Activity */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-8 shadow-lg flex flex-col h-full"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300">
                            <Clock className="w-5 h-5" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Recent Activity</h3>
                    </div>
                    <div className="space-y-6 flex-1">
                        <div className="flex justify-between items-center py-2 border-b border-slate-50 dark:border-slate-700/50">
                            <span className="text-slate-500 dark:text-slate-400 text-sm">Session Start</span>
                            <span className="text-slate-900 dark:text-white font-bold">{formatDate(new Date().toISOString())}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                            <span className="text-slate-500 dark:text-slate-400 text-sm">Last Seen Online</span>
                            <span className="text-slate-900 dark:text-white font-bold">{formatDate(user?.last_login)}</span>
                        </div>
                    </div>
                </motion.div>

                {/* Active Sessions */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-8 shadow-lg flex flex-col h-full"
                >
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300">
                                <Monitor className="w-5 h-5" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Active Devices</h3>
                        </div>
                        <button
                            onClick={handleLogoutAll}
                            className="text-[10px] font-black text-red-500 hover:text-red-100 hover:bg-red-500 uppercase tracking-widest bg-red-50 dark:bg-red-900/20 px-3 py-1.5 rounded-lg transition-all border border-red-100 dark:border-red-900/30"
                        >
                            Log Out All
                        </button>
                    </div>
                    <div className="space-y-3 flex-1 overflow-y-auto max-h-[220px] scrollbar-hide">
                        {activeSessions.length > 0 ? (
                            activeSessions.map((session, index) => (
                                <div key={index} className="flex flex-col gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-700">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-1.5 rounded-lg ${session.is_current ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'bg-slate-200 dark:bg-slate-800 text-slate-500'}`}>
                                            <Monitor className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-slate-900 dark:text-white text-xs truncate">{session.device}</p>
                                            <p className="text-[10px] text-slate-400 font-medium truncate">{session.ip_address}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700/50">
                                        <span className={`text-[10px] font-bold ${session.is_current ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500'}`}>
                                            {session.last_active}
                                        </span>
                                        {session.is_current && (
                                            <span className="text-[8px] font-black tracking-[0.1em] uppercase bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded-md">
                                                Active Now
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : loadingSessions ? (
                            <div className="text-center text-slate-500 text-sm py-8 flex flex-col items-center gap-3">
                                <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : (
                            <div className="text-center text-slate-400 text-xs py-8 font-medium">
                                No active devices found.
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>

            {/* Row 3: Security & Display (Centered) */}
            <div className="flex justify-center w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-5xl">
                    {/* Security Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-8 shadow-lg flex flex-col h-full"
                    >
                        <div className="flex items-center gap-3 mb-8">
                            <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300">
                                <Shield className="w-5 h-5" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Security Settings</h3>
                        </div>

                        <div className="space-y-4 flex-1">
                            {!isChangingPassword ? (
                                <button
                                    onClick={() => {
                                        setIsChangingPassword(true);
                                        setPasswordError("");
                                        setPasswordSuccess("");
                                    }}
                                    className="w-full flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-700 rounded-2xl hover:border-indigo-400 group transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <Lock className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors" />
                                        <span className="font-bold text-slate-700 dark:text-slate-200">Update Secure Password</span>
                                    </div>
                                    <ChevronDown className="w-4 h-4 text-slate-300 group-hover:text-indigo-500" />
                                </button>
                            ) : (
                                <motion.form
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="space-y-4 mb-4"
                                    onSubmit={handleChangePassword}
                                >
                                    <div className="relative">
                                        <input
                                            type={showOldPassword ? "text" : "password"}
                                            placeholder="Current Password"
                                            required
                                            value={passwordData.old_password}
                                            onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                                            className="w-full pl-4 pr-10 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500 transition-all text-sm font-medium"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowOldPassword(!showOldPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1"
                                            title={showOldPassword ? "Hide password" : "Show password"}
                                        >
                                            {showOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="relative">
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                placeholder="New Password"
                                                required
                                                value={passwordData.new_password}
                                                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                                                className="w-full pl-4 pr-10 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500 transition-all text-sm font-medium"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1"
                                                title={showNewPassword ? "Hide password" : "Show password"}
                                            >
                                                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                placeholder="Confirm"
                                                required
                                                value={passwordData.confirm_password}
                                                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                                                className="w-full pl-4 pr-10 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500 transition-all text-sm font-medium"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1"
                                                title={showConfirmPassword ? "Hide password" : "Show password"}
                                            >
                                                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {passwordError && (
                                        <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 rounded-xl text-xs font-semibold flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                            <span>{passwordError}</span>
                                        </div>
                                    )}

                                    {passwordSuccess && (
                                        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-xl text-xs font-semibold flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                                            <span>{passwordSuccess}</span>
                                        </div>
                                    )}

                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setIsChangingPassword(false)}
                                            className="flex-1 py-2 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg text-xs font-bold"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loadingPassword}
                                            className="flex-1 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-xs font-bold shadow-lg shadow-slate-900/10 dark:shadow-white/5 disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {loadingPassword ? (
                                                <>
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    <span>Updating...</span>
                                                </>
                                            ) : (
                                                "Confirm Update"
                                            )}
                                        </button>
                                    </div>
                                </motion.form>
                            )}

                            <div className="p-4 bg-slate-50/50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 rounded-2xl space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest">2FA Protection</span>
                                    <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 rounded-md text-[10px] font-black uppercase tracking-tighter">Enabled</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest">Last Password Change</span>
                                    <span className="text-xs font-black text-slate-900 dark:text-white">
                                        {formatDate(user?.last_password_updated) || "First Login"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Display Settings Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-8 shadow-lg flex flex-col h-full"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300">
                                <Monitor className="w-5 h-5" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Workspace Theme</h3>
                        </div>
                        <div className="grid grid-cols-1 gap-3 flex-1">
                            {themeOptions.map((opt) => (
                                <button
                                    key={opt.id}
                                    onClick={() => setTheme(opt.id)}
                                    className={`flex items-center justify-between p-4 rounded-2xl border transition-all duration-300 ${theme === opt.id
                                        ? "bg-slate-900 dark:bg-white border-slate-900 dark:border-white text-white dark:text-slate-900 shadow-xl shadow-slate-900/20"
                                        : "bg-gray-50 dark:bg-slate-900/30 border-slate-100 dark:border-slate-700/50 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600"
                                        }`}
                                >
                                    <div className="flex items-center gap-3 font-bold text-sm">
                                        <opt.icon className={`w-4 h-4 ${theme === opt.id ? "text-white dark:text-slate-900" : "text-slate-400 dark:text-slate-500"}`} />
                                        <span>{opt.label}</span>
                                    </div>
                                    {theme === opt.id && (
                                        <motion.div layoutId="active-theme-v3" className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Custom In-Box Logout All Devices Confirmation Modal */}
            <AnimatePresence>
                {showLogoutConfirmModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => !loggingOutSessions && setShowLogoutConfirmModal(false)}
                            className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm"
                        />

                        {/* Modal Box */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-200 dark:border-slate-800 z-10 overflow-hidden"
                        >
                            {/* Glow accent */}
                            <div className="absolute -top-12 -right-12 w-32 h-32 bg-red-500/10 rounded-full blur-2xl pointer-events-none" />

                            <div className="flex items-start gap-4 mb-4">
                                <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-2xl flex-shrink-0">
                                    <AlertTriangle className="w-6 h-6" />
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                        Log Out From All Devices?
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                                        Are you sure you want to log out from all active devices? This action will invalidate active sessions.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    type="button"
                                    disabled={loggingOutSessions}
                                    onClick={() => setShowLogoutConfirmModal(false)}
                                    className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={loggingOutSessions}
                                    onClick={confirmLogoutAllSessions}
                                    className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 active:scale-95 shadow-lg shadow-red-600/20 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {loggingOutSessions ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Logging out...</span>
                                        </>
                                    ) : (
                                        <>
                                            <LogOut className="w-4 h-4" />
                                            <span>Log Out All</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
